import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Award, Printer } from 'lucide-react';

import { getCertificate } from '@/api/training.api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { describeTrainingError } from '@/lib/training/errors';
import { useAuth } from '@/hooks/useAuth';

/**
 * Printable training certificate (Stream C, ADR-025). Everything on the
 * page is a SNAPSHOT from the TrainingCompletion record — trainee name,
 * course title, version, score — so it stays true after renames and
 * turnover. The certificate code is the verification key: anyone in the
 * org can look it up (self always; others need view_training_reports).
 *
 * Print: the app shell prints anyway, so the page's own chrome (back
 * link, print button) carries `print:hidden`; index.css hides leftover
 * buttons as a final fallback.
 */

function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

export function CertificatePage() {
  const { code } = useParams<{ code: string }>();
  const { state: authState } = useAuth();
  const orgName = authState.status === 'authenticated' ? authState.me.organization.name : '';

  const certificateQuery = useQuery({
    queryKey: ['training', 'certificate', code],
    queryFn: () => getCertificate(code ?? ''),
    enabled: Boolean(code),
  });

  if (certificateQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
      </div>
    );
  }

  if (certificateQuery.error || !certificateQuery.data) {
    return (
      <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
        <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
        <div className="flex-1">
          <p className="text-[14px] font-medium text-kw-danger-text">Certificate not found</p>
          <p className="mt-1 text-[13px] text-kw-danger-text/80">
            {describeTrainingError(certificateQuery.error, 'No certificate matches that code.')}
          </p>
          <Link to="/training" className="mt-3 inline-block text-[13px] text-kw-cerulean hover:underline">
            ← Back to My Training
          </Link>
        </div>
      </Card>
    );
  }

  const cert = certificateQuery.data;

  return (
    <div className="mx-auto max-w-3xl space-y-4 print:max-w-none">
      <div className="flex items-center justify-between print:hidden">
        <Link
          to="/training"
          className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:text-kw-shark"
        >
          <ArrowLeft className="size-4" />
          My Training
        </Link>
        <Button
          variant="secondary"
          onClick={() => {
            window.print();
          }}
        >
          <Printer className="size-4" />
          Print certificate
        </Button>
      </div>

      <Card className="p-10 text-center print:border-2 print:border-kw-shark print:shadow-none">
        <p className="text-[13px] font-medium uppercase tracking-[0.2em] text-kw-muted">{orgName}</p>
        <Award className="mx-auto mt-6 size-12 text-kw-cerulean" />
        <h1 className="mt-4 text-[26px] font-bold text-kw-shark">Certificate of Completion</h1>

        <p className="mt-8 text-[14px] text-kw-muted">This certifies that</p>
        <p className="mt-1 text-[22px] font-semibold text-kw-shark">{cert.traineeNameSnapshot}</p>
        <p className="mt-4 text-[14px] text-kw-muted">successfully completed the training course</p>
        <p className="mt-1 text-[18px] font-semibold text-kw-shark">
          {cert.courseTitleSnapshot}{' '}
          <span className="text-[13px] font-normal text-kw-muted">(v{cert.courseVersion})</span>
        </p>

        <div className="mx-auto mt-8 flex max-w-md flex-wrap items-center justify-center gap-x-8 gap-y-2 text-[13px] text-kw-shark">
          <p>
            <span className="text-kw-muted">Completed:</span> {longDate(cert.completedAt)}
          </p>
          {cert.quizScorePercent !== null ? (
            <p>
              <span className="text-kw-muted">Score:</span> {cert.quizScorePercent}%
            </p>
          ) : null}
          {cert.expiresAt ? (
            <p>
              <span className="text-kw-muted">Valid until:</span> {longDate(cert.expiresAt)}
            </p>
          ) : null}
        </div>

        <div className="mt-10 border-t border-kw-border pt-4">
          <p className="font-mono text-[13px] text-kw-shark">{cert.certificateCode}</p>
          <p className="mt-1 text-[11px] text-kw-muted">
            Certificate code — verify this record in the {orgName} staff portal under Training → Certificates.
          </p>
        </div>
      </Card>
    </div>
  );
}
